import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { Link as RouterLink } from 'react-router-dom'

import {
  campaignCardPrimaryHref,
  campaignCardTitle,
  LANDING_STATUS_PILL,
  resolveLandingCardStatus,
} from '@/features/campaigns/lib/campaign-landing-display'
import type { Campaign } from '@/features/campaigns/types'
import { SortableTableHeaderLabel } from '@/shared/table'
import { sensemakerTableBodyCellInteractiveSx, sensemakerTableSx } from '@/shared/table/sensemaker-table-theme'

import { formatAdminDashboardCreatedDate } from '../lib/format-admin-dashboard-date'
import {
  RECENT_CAMPAIGNS_TABLE,
  recentCampaignsActionsBodyCellSx,
  recentCampaignsActionsHeaderCellSx,
  recentCampaignsBodyCellSx,
  recentCampaignsHeaderCellSx,
} from '../lib/recent-campaigns-table-theme'
import { RecentCampaignRowActions } from './RecentCampaignRowActions'

interface Props {
  campaigns: Campaign[]
  nameSortDirection: 'asc' | 'desc' | null
  onToggleNameSort: () => void
}

export function RecentCampaignsTable({
  campaigns,
  nameSortDirection,
  onToggleNameSort,
}: Readonly<Props>) {
  return (
    <TableContainer sx={{ bgcolor: RECENT_CAMPAIGNS_TABLE.bodyBg }}>
      <Table
        size="small"
        data-testid="admin-dashboard-campaigns-table"
        sx={sensemakerTableSx}
      >
        <colgroup>
          <col style={{ width: RECENT_CAMPAIGNS_TABLE.colNameWidth }} />
          <col style={{ width: RECENT_CAMPAIGNS_TABLE.colStatusWidth }} />
          <col style={{ width: RECENT_CAMPAIGNS_TABLE.colDateWidth }} />
          <col style={{ width: RECENT_CAMPAIGNS_TABLE.colActionsWidth }} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell sx={recentCampaignsHeaderCellSx}>
              <SortableTableHeaderLabel
                label="Campaign Name"
                sortDirection={nameSortDirection}
                testId="admin-dashboard-campaigns-name-sort"
                onToggle={onToggleNameSort}
              />
            </TableCell>
            <TableCell sx={recentCampaignsHeaderCellSx}>Status</TableCell>
            <TableCell sx={recentCampaignsHeaderCellSx}>Created date</TableCell>
            <TableCell sx={recentCampaignsActionsHeaderCellSx}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {campaigns.map((campaign) => {
            const cardStatus = resolveLandingCardStatus(campaign)
            const pill = LANDING_STATUS_PILL[cardStatus]
            const title = campaignCardTitle(campaign)
            const nameHref = campaignCardPrimaryHref(campaign)

            return (
              <TableRow
                key={campaign.id}
                hover={false}
                data-testid={`admin-dashboard-campaign-row-${campaign.id}`}
                sx={{ bgcolor: RECENT_CAMPAIGNS_TABLE.bodyBg }}
              >
                <TableCell sx={recentCampaignsBodyCellSx}>
                  <Tooltip title={title} placement="top-start">
                    <Link
                      component={RouterLink}
                      to={nameHref}
                      underline="hover"
                      data-testid={`admin-dashboard-campaign-name-${campaign.id}`}
                      sx={{
                        fontSize: '14px',
                        fontWeight: 700,
                        lineHeight: '20px',
                        color: RECENT_CAMPAIGNS_TABLE.nameColor,
                        display: 'inline-block',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        verticalAlign: 'middle',
                        '&:hover': { color: '#008cd0' },
                      }}
                    >
                      {title}
                    </Link>
                  </Tooltip>
                </TableCell>
                <TableCell sx={sensemakerTableBodyCellInteractiveSx}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      height: 26,
                      px: '12px',
                      py: '4px',
                      borderRadius: 9999,
                      border: `1px solid ${pill.border}`,
                      bgcolor: pill.bg,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 500,
                        lineHeight: '16px',
                        color: pill.color,
                      }}
                    >
                      {pill.label}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    ...recentCampaignsBodyCellSx,
                    color: RECENT_CAMPAIGNS_TABLE.dateColor,
                    fontSize: '14px',
                    lineHeight: '20px',
                    letterSpacing: '-0.1504px',
                  }}
                >
                  {formatAdminDashboardCreatedDate(campaign.createdAt)}
                </TableCell>
                <TableCell sx={recentCampaignsActionsBodyCellSx}>
                  <RecentCampaignRowActions campaign={campaign} />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
